import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, ClipboardCheck, Stethoscope, ShieldCheck,
  AlertTriangle, CheckCircle2, XCircle, Users, BedDouble, Activity, FilePlus2, FileWarning,
  HomeIcon, Clock, RotateCcw,
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import KPICard from "@/components/common/KPICard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import WorkflowBadge from "@/pages/onboarding/WorkflowBadge";
import { canCreateRequest } from "@/pages/onboarding/constants";

const PIE_COLORS = ["#0B7E8C", "#128C97", "#06B6D4", "#6366F1", "#F59E0B", "#10B981", "#94A3B8", "#EF4444"];

const KPI_MAP = {
  // hospital coordinator
  draft: { label: "Draft Requests", icon: ClipboardList, accent: "#94A3B8" },
  submitted: { label: "Submitted", icon: ClipboardList, accent: "#06B6D4" },
  information_requested: { label: "Information Requested", icon: FileWarning, accent: "#F59E0B" },
  medical_approved: { label: "Medical Approved", icon: Stethoscope, accent: "#0B7E8C" },
  operationally_approved: { label: "Operationally Approved", icon: ShieldCheck, accent: "#10B981" },
  assigned: { label: "Assigned", icon: BedDouble, accent: "#0B7E8C" },
  completed: { label: "Completed", icon: CheckCircle2, accent: "#10B981" },
  rejected: { label: "Rejected", icon: XCircle, accent: "#EF4444" },
  // physician
  pending_reviews: { label: "Pending Reviews", icon: Stethoscope, accent: "#6366F1" },
  readmissions_pending: { label: "Readmissions Pending", icon: RotateCcw, accent: "#D97706" },
  approved: { label: "Approved", icon: CheckCircle2, accent: "#10B981" },
  // hah coordinator
  pending_assessments: { label: "Pending Assessments", icon: ClipboardCheck, accent: "#6366F1" },
  awaiting_assignment: { label: "Awaiting Assignment", icon: AlertTriangle, accent: "#F59E0B" },
  assigned_active: { label: "Active Assignments", icon: Activity, accent: "#0B7E8C" },
  visit_completed: { label: "Visits Completed", icon: CheckCircle2, accent: "#10B981" },
  // home care team
  assigned_visits: { label: "Assigned Visits", icon: BedDouble, accent: "#0B7E8C" },
  todays_visits: { label: "Today's Visits", icon: HomeIcon, accent: "#06B6D4" },
  in_progress: { label: "Visit In Progress", icon: Activity, accent: "#6366F1" },
  overdue: { label: "Overdue", icon: AlertTriangle, accent: "#EF4444" },
  // admin fallback
  total: { label: "Total Requests", icon: ClipboardList, accent: "#0B7E8C" },
  active: { label: "In Workflow", icon: Activity, accent: "#06B6D4" },
};


function SubtitleByRole({ role }) {
  if (role === "hospital_coordinator") return "Manage your patient referrals into the H@H program.";
  if (role === "hah_physician" || role === "physician") return "Clinical referrals awaiting your medical decision.";
  if (role === "hah_coordinator" || role === "care_coordinator") return "Initial assessments, operational approvals, and team assignments.";
  if (role === "home_care_team") return "Your assigned in-home visits and findings.";
  return "All onboarding requests across the H@H network.";
}


export default function OnboardingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role_code || "viewer";

  useEffect(() => {
    api.get("/onboarding/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const kpis = data?.kpis || {};
  const kpiEntries = Object.entries(kpis);

  return (
    <div data-testid="onboarding-dashboard">
      <PageHeader
        title="Onboarding Command Center"
        subtitle={<SubtitleByRole role={role} />}
        actions={
          canCreateRequest(role) && (
            <Button
              data-testid="ob-new-request"
              onClick={() => navigate("/onboarding/new")}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            >
              <FilePlus2 className="w-4 h-4 mr-1.5" /> New Onboarding Request
            </Button>
          )
        }
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpiEntries.map(([k, v]) => {
          const cfg = KPI_MAP[k] || { label: k.replace(/_/g, " "), icon: ClipboardList, accent: "#0B7E8C" };
          return (
            <KPICard
              key={k}
              testId={`ob-kpi-${k}`}
              icon={cfg.icon}
              label={cfg.label}
              value={v}
              accent={cfg.accent}
            />
          );
        })}
      </div>

      {/* Operational widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        {/* Recent worklist */}
        <div className="hah-card p-5 lg:col-span-2" data-testid="ob-worklist">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="hah-section-title">What needs attention</div>
              <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Your worklist</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-[#E5E7EB]"
              data-testid="ob-view-all"
              onClick={() => navigate("/onboarding/requests")}
            >
              View all
            </Button>
          </div>
          <div className="space-y-2">
            {(data?.recent || []).length === 0 && (
              <div className="text-sm text-[#6B7280] py-6 text-center">Nothing assigned to you yet.</div>
            )}
            {(data?.recent || []).map((r) => (
              <button
                key={r.id}
                onClick={() => navigate(`/onboarding/requests/${r.id}`)}
                data-testid={`ob-worklist-${r.id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-lg border border-[#E5E7EB] hover:bg-[#EAF8FA]/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center font-semibold text-sm">
                  {(r.patient?.first_name?.[0] || "?") + (r.patient?.last_name?.[0] || "")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#1F2937] truncate">
                      {r.patient?.first_name} {r.patient?.last_name}
                    </span>
                    <span className="text-xs text-[#6B7280]">{r.ref_number}</span>
                    {r.status === "readmit_pending" && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#D9770614] text-[#B45309] border border-[#D9770633] inline-flex items-center gap-0.5" data-testid={`ob-worklist-readmit-${r.id}`}>
                        <RotateCcw className="w-2.5 h-2.5" /> Readmission
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#6B7280] truncate">
                    {r.clinical?.primary_diagnosis || "—"}
                  </div>
                </div>
                <WorkflowBadge status={r.status} />
                <div className="text-xs text-[#6B7280] hidden md:flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ""}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Workflow distribution */}
        <div className="hah-card p-5" data-testid="ob-distribution">
          <div className="hah-section-title">Workflow</div>
          <h3 className="text-base font-semibold text-[#1F2937] mt-0.5">Status distribution</h3>
          <div className="h-56 mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data?.distribution || []} dataKey="value" nameKey="name" innerRadius={42} outerRadius={72} paddingAngle={2}>
                  {(data?.distribution || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5 mt-2 max-h-[160px] overflow-y-auto">
            {(data?.distribution || []).map((row, i) => (
              <div key={row.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-[#1F2937]">{(row.name || "").replace(/_/g, " ")}</span>
                </div>
                <span className="font-medium text-[#1F2937]">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm text-[#6B7280]" data-testid="ob-loading">Loading…</div>
      )}
    </div>
  );
}
